import { Flags } from '@fullstory/client-api';
import * as schema from '@fullstory/client-api/goimports/fs_shared_schema';
import { NamedElementMatcher } from '@fullstory/named-elements';
import {
  BuiltInDimension,
  DataRow,
  DataRowGroup,
  Dimension,
  DimensionSettings,
} from '@fullstory/proto/fs/api/metrics';
import { DependentEventFilter, Event } from '@fullstory/proto/fs/api/segment';
import {
  CustomField,
  parseUnscopedFieldName,
  VarType,
  FieldScope,
  unscopedCustomFieldIdent,
  SetFieldScope,
} from '@fullstory/search-vars';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { assertExhaustive } from '@fullstory/typeutils';
import { getPageName, renderedPageDefPattern } from './pages';
import {
  builtInDimensions,
  builtInWithoutContextual,
  cityDimension,
  cityStateDimension,
  contextualDimensions,
  DimensionalityPropertyOption,
  DimensionConfig,
  DimensionNoConfig,
  DimensionPillCategory,
  DimensionPillLabel,
  DimensionState,
  ERROR_CLICK_TYPE,
  LabelGroup,
  MOBILE_EXCEPTION_ONLY,
  NATIVE_MOBILE_ONLY,
  UTM_PREFIXES,
  WEB_EXCEPTION_ONLY,
} from '../types/dimensionality';
import {
  DEFAULT_DIMENSION_SETTINGS,
  DimensionSettingsNoDimensions,
  UNKNOWN_LABEL,
} from '../utils/constants';
import {
  DataRowWithId,
  definedEventSelectors,
  BySlug,
  SpecObjectInfo,
  allSpecObjectsBySlug,
} from '@fullstory/redux';
import { cloneDeep, isEqual } from 'lodash';
import { useFlags } from '@fullstory/react-hooks';
import { useMemo } from 'react';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { intl } from '@fullstory/intl';
import { defineMessage } from 'react-intl';
import { WithID } from '@fullstory/util/uid';
import { DependentUrlKinds } from '@fullstory/search-types';
import { useSelector } from 'react-redux';
import {
  DefinedEventRecord,
  SpecObjectProperty,
} from '@fullstory/proto/fs/services/search-settings';
import { forEachLeafProtoEvent } from './event';
import { fractionalChange } from '@fullstory/util/fmt';
import * as vars from '@fullstory/proto/fs/api/vars';

const { getDefinedEventsById } = definedEventSelectors;

export const dimensionsToDimensionalityOption = (
  dimensionState: DimensionState | null,
  specObjectsBySlug: BySlug<SpecObjectInfo>,
  specPropertiesBySlug: BySlug<SpecObjectProperty>,
): DimensionalityPropertyOption | null => {
  const dimension = dimensionState?.dimensions[0]; // for now, only builtIn will utilize multiple dimensions
  if (!dimension) {
    return null;
  }
  const oneof = Dimension.property_oneof({
    ...dimension,
    limit: 0,
    computeAtLevel: false,
    eventRefinement: null,
  });
  if (!oneof) {
    return null;
  }
  switch (oneof.opt) {
    case Dimension.property.builtInProperty:
      return builtInDimensionToDimensionalityOption(dimensionState!);
    case Dimension.property.specProperty: {
      if (
        oneof.val.asScope === vars.Scope.INVALID_SCOPE ||
        oneof.val.asType === vars.Type.INVALID_TYPE
      ) {
        return null;
      }
      const property = specPropertiesBySlug[oneof.val.specSlug];
      if (!property) {
        return null;
      }
      const asScope = vars.Scope[oneof.val.asScope] as SetFieldScope;
      const asType = vars.Type[oneof.val.asType] as VarType;
      const lastSepIdx = property.slug.lastIndexOf('/');
      const parentSlug = lastSepIdx > 0 ? property.slug.slice(0, lastSepIdx) : '';
      const parentDisplayName = specObjectsBySlug[parentSlug]?.displayName || parentSlug;
      return specToDimensionalityOption(property, asScope, asType, parentDisplayName);
    }
    default:
      // remainder are all custom property types that can be handled the same
      return fieldNameToDimensionalityOption(oneof.val, oneof.key);
  }
};

export const fieldNameToDimensionalityOption = (
  fieldName: string,
  type: Dimension.property_keys,
): DimensionalityPropertyOption => {
  const field = parseUnscopedFieldName(fieldName, FieldScope.NoScope);
  const fieldSuffix = getFieldSuffix(field);
  return {
    dimensions: [
      {
        [type]: fieldName,
        prefix: '',
      },
    ],
    id: `${type}:${unscopedCustomFieldIdent(field)}`,
    field,
    label: `${field.ident} (${fieldSuffix})`,
    type,
  };
};

export const specToDimensionalityOption = (
  property: SpecObjectProperty,
  asVarScope: SetFieldScope,
  asVarType: VarType,
  parentDisplayName: string,
): DimensionalityPropertyOption => {
  const label = property.metadata?.displayName || property.metadata?.name || property.slug;
  return {
    dimensions: [
      {
        specProperty: {
          specSlug: property.slug,
          asScope: vars.Scope[asVarScope],
          asType: vars.Type[asVarType],
        },
        prefix: '',
      },
    ],
    id: `spec:${property.slug}:${asVarType}`,
    spec: {
      parentDisplayName,
    },
    label,
    type: Dimension.property_keys.specProperty,
  };
};

const getFieldSuffix = (field: CustomField): string => {
  switch (field.varType) {
    case VarType.Bool:
      return intl.formatMessage({
        defaultMessage: 'bool',
        id: 'p2uzNw',
        description: 'Data type of a custom field',
      });
    case VarType.Bools:
      return intl.formatMessage({
        defaultMessage: 'bools',
        id: 'Zs4bLk',
        description: 'Data type of a custom field',
      });
    case VarType.Date:
      return intl.formatMessage({
        defaultMessage: 'date',
        id: '25xj3p',
        description: 'Data type of a custom field',
      });
    case VarType.Dates:
      return intl.formatMessage({
        defaultMessage: 'dates',
        id: 'S/oc/o',
        description: 'Data type of a custom field',
      });
    case VarType.Int:
      return intl.formatMessage({
        defaultMessage: 'int',
        id: 'UTVV74',
        description: 'Data type of a custom field',
      });
    case VarType.Ints:
      return intl.formatMessage({
        defaultMessage: 'ints',
        id: 'WJo8hs',
        description: 'Data type of a custom field',
      });
    case VarType.Real:
      return intl.formatMessage({
        defaultMessage: 'real',
        id: 'y9vkv5',
        description: 'Data type of a custom field',
      });
    case VarType.Reals:
      return intl.formatMessage({
        defaultMessage: 'reals',
        id: 'pfhBoK',
        description: 'Data type of a custom field',
      });
    case VarType.Str:
      return intl.formatMessage({
        defaultMessage: 'string',
        id: '+cBhEj',
        description: 'Data type of a custom field',
      });
    case VarType.Strs:
      return intl.formatMessage({
        defaultMessage: 'strings',
        id: 'vyTQ4E',
        description: 'Data type of a custom field',
      });
    default:
      assertExhaustive(field.varType);
  }
};

export const labelFromGroups = (groups: string[], delimiter: string = ', ') => {
  const mapped = groups.map(g => g || UNKNOWN_LABEL);
  // If all values are unknown, return one label of "Unknown"
  if (mapped.every(g => g === UNKNOWN_LABEL)) {
    return UNKNOWN_LABEL;
  }
  return mapped.join(delimiter);
};

const computeDimensionPillLabel = (
  dimensionState: DimensionState | null,
  groups: string[],
  fallback: boolean,
  matcher: NamedElementMatcher,
  pageDefs: Map<number, PageDefinition>,
): DimensionPillLabel => {
  const dimension = dimensionState?.dimensions[0];
  const label = labelFromGroups(groups, dimensionState?.delimiter);
  return computeDimensionLabelFromBuiltInProperty(
    dimension?.builtInProperty,
    fallback,
    label,
    matcher,
    pageDefs,
  );
};

export const computeDimensionLabelFromBuiltInProperty = (
  builtInProperty: BuiltInDimension | undefined,
  fallback: boolean,
  label: string,
  matcher: NamedElementMatcher,
  pageDefs: Map<number, PageDefinition>,
): DimensionPillLabel => {
  switch (builtInProperty) {
    case BuiltInDimension.DIMENSION_CSS: {
      if (fallback) {
        // named element pills turned off for raw css results
        return {
          value: '',
          category: DimensionPillCategory.invalid,
        };
      } else {
        const namedElementRecord = matcher.matchById(label);
        return {
          value: namedElementRecord?.namedElement?.name ?? '',
          category: DimensionPillCategory.namedElement,
          namedElement: namedElementRecord,
        };
      }
    }
    case BuiltInDimension.DIMENSION_PAGE_DEF_ID: {
      const pageDef = pageDefs.get(parseInt(label, 10));
      return {
        value: pageDef ? getPageName(pageDef) : '',
        category: DimensionPillCategory.pageDef,
        pageDef,
      };
    }
    default: {
      return {
        value: '',
        category: DimensionPillCategory.invalid,
      };
    }
  }
};

export const computeDimensionLabel = (
  dimensionState: DimensionState | null,
  groups: string[],
  pageDefs: Map<number, PageDefinition>,
): string => {
  // todo(kevin): we may want more context of rendering based on multiple dimensions
  const dimension = dimensionState?.dimensions[0];
  const label = labelFromGroups(groups, dimensionState?.delimiter);
  if (dimension?.builtInProperty === BuiltInDimension.DIMENSION_ERROR_CLICK_TYPE) {
    const errorClickCode: schema.ErrorClickCode = Number(label) as schema.ErrorClickCode;
    if (!Object.values(schema.ErrorClickCode).includes(errorClickCode)) {
      return '';
    }

    switch (errorClickCode) {
      case schema.ErrorClickCode.ERROR_CLICK_CODE_CONSOLE:
        return ERROR_CLICK_TYPE.console;
      case schema.ErrorClickCode.ERROR_CLICK_CODE_EXCEPTION:
        return ERROR_CLICK_TYPE.exception;
      case schema.ErrorClickCode.ERROR_CLICK_CODE_UNKNOWN:
        return ERROR_CLICK_TYPE.unknown;
      default:
        return '';
    }
  }

  if (dimension?.builtInProperty === BuiltInDimension.DIMENSION_PAGE_DEF_ID) {
    const maybePageDef = pageDefs.get(parseInt(label, 10));
    if (maybePageDef) {
      const firstLabel = maybePageDef.labels[0];
      if (firstLabel) return firstLabel.pageName;
    }
    return maybePageDef ? renderedPageDefPattern(maybePageDef) : '';
  }

  return label;
};

const previousFormatter = defineMessage({
  defaultMessage: '{value} (Previous)',
  id: 'LrgAQe',
  description: 'Appends "Previous" suffix for past dimensions',
});
export const computeDimensionLabelGroupFromResults = (
  results: DataRowWithId[],
  dimensionState: DimensionState | null | undefined,
  matcher: NamedElementMatcher,
  pageDefs: Map<number, PageDefinition>,
  flags: Flags,
  pastResults?: DataRowWithId[],
): LabelGroup[] => {
  const dimensionValues: LabelGroup[] = [];
  const allResults = pastResults ? results.concat(pastResults) : results;
  allResults
    .filter(data => !!data.groups.length) // datum that doesn't have a defined group is an aggregate number
    .sort((a, b) => b.value - a.value)
    .map(data => {
      const dimensionLabelGroup = computeDimensionLabelGroupFromResult(
        data,
        dimensionState ?? null,
        matcher,
        pageDefs,
      );
      const { groups, fallback } = dimensionLabelGroup;
      let pillLabel = dimensionLabelGroup.pillLabel;
      let label = dimensionLabelGroup.label;
      const isPast = pastResults?.find(
        d =>
          isEqual(
            d.groups.map(g => g.groupId),
            groups,
          ) && d.value === data.value,
      );
      if (isPast) {
        label = intl.formatMessage(previousFormatter, { value: dimensionLabelGroup.label });
        if (pillLabel?.value) {
          pillLabel = {
            ...pillLabel,
            value: intl.formatMessage(previousFormatter, { value: pillLabel.value }),
          };
        }
      }
      if (
        !dimensionValues.find(
          d =>
            d.label === label ||
            (!!pillLabel?.value &&
              !!d.pillLabel?.value &&
              pillLabel.value === d.pillLabel.value &&
              d.groups.every((val, i) => val == groups[i])),
        )
      ) {
        dimensionValues.push({
          groups,
          id: data.id,
          label,
          pillLabel,
          fallback,
        });
      }
    });
  return dimensionValues;
};

export const computeDimensionLabelGroupFromGroups = (
  id: string,
  dataRowGroups: DataRowGroup[],
  dimensionState: DimensionState | null | undefined,
  matcher: NamedElementMatcher,
  pageDefs: Map<number, PageDefinition>,
): LabelGroup => {
  // TODO(kevin): different dimension values can return different fallbacks, change structure to account for this
  const fallback = !!dataRowGroups[0]?.fallback;
  const groups = dataRowGroups.map(g => g.groupId);
  const label = computeDimensionLabel(dimensionState ?? null, groups, pageDefs) || UNKNOWN_LABEL;
  const pillLabel = computeDimensionPillLabel(
    dimensionState ?? null,
    groups,
    fallback,
    matcher,
    pageDefs,
  );
  return { groups, id, label, pillLabel, fallback };
};

export const computeDimensionLabelGroupFromResult = (
  result: WithID<Pick<DataRow, 'groups'>>,
  dimensionState: DimensionState | null | undefined,
  matcher: NamedElementMatcher,
  pageDefs: Map<number, PageDefinition>,
): LabelGroup =>
  computeDimensionLabelGroupFromGroups(result.id, result.groups, dimensionState, matcher, pageDefs);

// given a list of groups and isPast boolean, return a string that can be used as a unique id for the dimension group
export const buildGroupId = (groups: string[], isPast: boolean): string => {
  const groupList = groups.join('-');
  return isPast ? `${groupList}-past` : groupList;
};

const getContextualOptions = (
  events: Event[],
  definedEventsById: Map<string, DefinedEventRecord>,
  specObjects: BySlug<SpecObjectInfo>,
): DimensionalityPropertyOption[] => {
  const dims: DimensionState[] = [];
  const config = {
    includeApiEvent: false,
    includeErrorType: false,
    includeWebSourceUrl: false,
    includeNetworkError: false,
  };
  for (const event of events) {
    // Use this helper to check all events from unions and defined events
    forEachLeafProtoEvent(event, definedEventsById, specObjects, (e: Event) => {
      if (e.primary?.custom) {
        config.includeApiEvent = true;
      }
      if (e.primary?.errorClick) {
        config.includeErrorType = true;
      }
      if (e.primary?.uncaughtException) {
        config.includeWebSourceUrl = true;
      }
      if (e.primary?.ajaxErrorUrl) {
        config.includeNetworkError = true;
      }
    });
  }

  if (config.includeApiEvent) {
    dims.push(...contextualDimensions.apiEvent);
  }
  if (config.includeErrorType) {
    dims.push(...contextualDimensions.errorType);
  }
  if (config.includeWebSourceUrl) {
    dims.push(...contextualDimensions.uncaughtException);
  }
  if (config.includeNetworkError) {
    dims.push(...contextualDimensions.networkError);
  }
  return dims.map(dim => builtInDimensionToDimensionalityOption(dim));
};

// If we have a null primary, then it is an Any Activity, so include all dimensions
const includeBuiltInWithContextual = (events: Event[]) => events.some(event => !event.primary);

export const useBuiltInOptions = (
  events: Event[],
): {
  builtInOptions: DimensionalityPropertyOption[];
  contextualOptions: DimensionalityPropertyOption[];
} => {
  const flags = useFlags();
  const definedEventsById = useSelector(getDefinedEventsById);
  const specObjects = useSelector(allSpecObjectsBySlug);

  return useMemo(() => {
    let options = includeBuiltInWithContextual(events)
      ? builtInDimensions
      : builtInWithoutContextual;

    if (!flags?.hasBit(Flag.FeatNativeMobile)) {
      options = options.filter(
        value => !value.dimensions.some(d => NATIVE_MOBILE_ONLY.includes(d.builtInProperty!)),
      );
    }

    if (!flags?.hasBit(Flag.FeatDevtools)) {
      options = options.filter(
        value => !value.dimensions.some(d => WEB_EXCEPTION_ONLY.includes(d.builtInProperty!)),
      );
    }

    if (!flags?.hasBit(Flag.FeatNativeMobile) || !flags?.hasBit(Flag.FeatDevtools)) {
      options = options.filter(
        value => !value.dimensions.some(d => MOBILE_EXCEPTION_ONLY.includes(d.builtInProperty!)),
      );
    }

    if (!flags.featureFlags().AppDependentUserFilters) {
      options = options.filter(
        value =>
          !value.dimensions.some(d => d.builtInProperty === BuiltInDimension.DIMENSION_USER_ID),
      );
    }

    if (!flags?.hasBit(Flag.FeatNativeMobile) || !flags?.featureFlags().AppFsSdkVersion) {
      options = options.filter(
        value =>
          !value.dimensions.some(
            d => d.builtInProperty === BuiltInDimension.DIMENSION_APP_FS_VERSION,
          ),
      );
    }

    // Legacy city dimension is still used to identify existing metrics, but always filter it out of the dimension group
    options = options.filter(value => !isDimensionStateEqual(value, cityDimension));

    return {
      builtInOptions: options.map(value => builtInDimensionToDimensionalityOption(value)),
      contextualOptions: getContextualOptions(events, definedEventsById, specObjects),
    };
  }, [flags, events, definedEventsById, specObjects]);
};

// dimensionsFromDimensionSettings returns an array from either the dimensions field or the legacy field
// it also interleaves the noFallback value
export const dimensionsFromDimensionSettings = (
  dimSettings: DimensionSettings | null | undefined,
): Dimension[] =>
  dimSettings?.dimensions?.length
    ? dimSettings.dimensions
    : dimSettings?.dimension
    ? [dimSettings.dimension]
    : [];

export const isCannedUTMUrlQuery = (dimension: DimensionNoConfig | null | undefined) =>
  dimension?.builtInProperty === BuiltInDimension.DIMENSION_URL_QUERY &&
  Object.values(UTM_PREFIXES).some(prefix => prefix === dimension?.prefix);

export const queryValueDimensions = new Map<BuiltInDimension, DependentUrlKinds>([
  [BuiltInDimension.DIMENSION_URL_QUERY, DependentEventFilter.filter_keys.url],
  [BuiltInDimension.DIMENSION_URL_HASH_QUERY, DependentEventFilter.filter_keys.url],
  [BuiltInDimension.DIMENSION_REFERRER_QUERY, DependentEventFilter.filter_keys.referrerUrl],
  [
    BuiltInDimension.DIMENSION_WEB_SOURCE_FILE_URL_QUERY,
    DependentEventFilter.filter_keys.webSourceFileUrl,
  ],
  [
    BuiltInDimension.DIMENSION_AJAX_REQUEST_URL_QUERY,
    DependentEventFilter.filter_keys.ajaxRequestUrl,
  ],
]);

export const isBuiltInQueryDim = (
  dimension: DimensionNoConfig | null | undefined,
): dimension is DimensionNoConfig =>
  !!dimension?.builtInProperty && queryValueDimensions.has(dimension.builtInProperty);

// dimensionsToDimensionState returns the DimensionState from input dimensions, which may be loaded upon refresh
export const dimensionsToDimensionState = (
  dimensions: Dimension[],
  config: DimensionConfig,
): DimensionState | null => {
  const { noFallback } = config;
  if (!dimensions.length) return null;
  const dimensionsNoConfig = dimensions.map(d => dimensionNoConfig(d));

  for (const dim of dimensions) {
    const oneof = Dimension.property_oneof(dim);
    if (oneof?.opt !== Dimension.property.builtInProperty) {
      if (oneof?.opt === Dimension.property.specProperty) {
        // spec properties have a bit more structured of a value
        return {
          dimensions: dimensionsNoConfig,
          noFallback,
          label: oneof.val.specSlug,
        };
      } else {
        // non built ins are custom properties whose value is a string
        return {
          dimensions: dimensionsNoConfig,
          noFallback,
          label: oneof?.val ?? '',
        };
      }
    }
  }

  const dims = cloneDeep(dimensionsNoConfig);
  dims.forEach(dim => {
    dim.prefix = dim.prefix ?? ''; // populate null/undefined prefixes
  });
  if (isBuiltInQueryDim(dims[0]) && !isCannedUTMUrlQuery(dims[0])) {
    dims[0].prefix = '';
  }

  let dimensionState = cloneDeep(
    builtInDimensions.find(d => areDimensionsEqual(d.dimensions, dims)),
  );
  if (dimensionState === undefined) {
    console.error(
      `could not find dimension option for input dimensions [${dimensionsNoConfig
        .map(d => BuiltInDimension[d.builtInProperty!])
        .join(', ')}]`,
    );
    return null;
  }
  dimensionState.dimensions.forEach((dim, i) => {
    dim.prefix = dimensionsNoConfig[i]?.prefix ?? '';
  });

  // For legacy City dimension, possibly replace the dimension with multi
  if (isDimensionStateEqual(dimensionState, cityDimension)) {
    dimensionState = cityStateDimension;
  }

  return {
    ...dimensionState,
    noFallback,
  };
};

export const builtInDimensionToDimensionalityOption = (
  dimensionState: DimensionState,
): DimensionalityPropertyOption => ({
  ...dimensionState,
  id: dimensionState.dimensions
    .map(d => `${BuiltInDimension[d.builtInProperty!]}${d.prefix}`)
    .toString(),
  type: Dimension.property_keys.builtInProperty,
});

export const dimensionSettingsFromDimensionState = (
  dimensionState: DimensionState | null | undefined,
  dimensionSettings?: DimensionSettingsNoDimensions,
): DimensionSettings | null => {
  const settings = dimensionSettings ?? DEFAULT_DIMENSION_SETTINGS;
  const dimensions = dimensionState?.dimensions?.map(d => ({
    ...d,
    limit: 0,
    computeAtLevel: false,
    eventRefinement: null,
  }));
  const dimension = dimensions?.[0];
  return dimensionState && dimension
    ? {
        ...settings,
        dimensions,
        dimension,
        noFallback: !!dimensionState.noFallback,
      }
    : null;
};

export const settingsHasDimension = (
  dimensionSettings: DimensionSettings | null | undefined,
): boolean => !!(dimensionSettings?.dimension || dimensionSettings?.dimensions?.length);

export const UNKNOWN_LABEL_PREVIOUS = intl.formatMessage(previousFormatter, {
  value: UNKNOWN_LABEL,
});

export const isUnknownLabel = (label: string) =>
  label === UNKNOWN_LABEL || label === UNKNOWN_LABEL_PREVIOUS;

export function getChangeValue(
  value?: number,
  prevValue?: number,
  numberStyle?: 'decimal' | 'percent',
) {
  let change = null;
  if (prevValue !== undefined && value !== undefined) {
    if (numberStyle === 'decimal') {
      change = value - prevValue;
    } else if (prevValue !== 0) {
      change = fractionalChange(value, prevValue);
    }
  }

  return change;
}

export function isDimensionStateEqual(a: DimensionState, b: DimensionState) {
  const aNoConfig: DimensionState = {
    ...a,
    dimensions: a.dimensions.map(d => dimensionNoConfig(d)),
  };
  const bNoConfig: DimensionState = {
    ...b,
    dimensions: b.dimensions.map(d => dimensionNoConfig(d)),
  };
  return isEqual(aNoConfig, bNoConfig);
}

export function areDimensionsEqual(a: DimensionNoConfig[], b: DimensionNoConfig[]) {
  const aNoConfig = a.map(d => dimensionNoConfig(d));
  const bNoConfig = b.map(d => dimensionNoConfig(d));
  return isEqual(aNoConfig, bNoConfig);
}

// isDimensionIdentityEqual returns true if the two dimensions are the same identity, ignoring config options.
export function isDimensionIdentityEqual(a: DimensionNoConfig, b: DimensionNoConfig) {
  return isEqual(dimensionNoConfig(a), dimensionNoConfig(b));
}

export function dimensionNoConfig(d: DimensionNoConfig): DimensionNoConfig {
  // picking fields explicitly to be safe in case extra config options are part of the struct
  // (since that would still adhere to the type but not match equality)
  const noConfig: DimensionNoConfig = { prefix: d.prefix };
  if (d.builtInProperty) {
    noConfig.builtInProperty = d.builtInProperty;
  }
  if (d.customEventProperty) {
    noConfig.customEventProperty = d.customEventProperty;
  }
  if (d.userVarProperty) {
    noConfig.userVarProperty = d.userVarProperty;
  }
  if (d.pageProperty) {
    noConfig.pageProperty = d.pageProperty;
  }
  if (d.elementProperty) {
    noConfig.elementProperty = d.elementProperty;
  }
  if (d.specProperty) {
    noConfig.specProperty = d.specProperty;
  }
  return noConfig;
}
